/**
 * useGameActions - Hook für Spielaktionen (Play, Draw, Pass, Shield, Peek, SealRing)
 *
 * Extrahiert die Spielaktions-Logik aus App.js für bessere Wartbarkeit.
 *
 * MIGRATION: Verwendet jetzt activePlayerIndex/isTestMode statt isLocalTestMode/myPlayerIndex
 */

import { useCallback } from 'react';
import socketService from '../socketService';
import soundService from '../soundService';
// getPlayType removed - was unused
import { SEAL_TRIGGER } from '../shared/entities/seals';

/**
 * @param {Object} config - Konfiguration mit State und Setters
 */
export function useGameActions(config) {
  const {
    // State
    selectedCards,
    setSelectedCards,
    gameState,
    activePlayerIndex,  // From useActivePlayer - the currently viewing/acting player
    isTestMode,         // From useActivePlayer - whether in test mode
    isRoundStarter,
    shieldUses,
    peekUses,
    sealRingUses,
    magnetUses,
    swapUses,
    gluecksgriffUses,
    stash, // For Glücksgriff stash check
    sealedCards, // For HAND seal fade animation

    // Setters
    setPlayingCards,
    flashMessage, // New: flashMessage from useStatusMessage hook
    setFadingSeals, // For HAND seal fade animation

    // Refs
    lastPlayRef,
  } = config;

  // In test mode, we send the player index to the server
  // In multiplayer, we send null (server knows who we are)
  const getForPlayerIndex = useCallback(() => {
    return isTestMode ? activePlayerIndex : null;
  }, [isTestMode, activePlayerIndex]);

  /**
   * Karten spielen
   */
  const makePlay = useCallback(() => {
    if (selectedCards.length === 0) return;

    // Check if any selected card has a HAND seal (for fade animation)
    const handSealCards = selectedCards.filter(card => {
      if (!sealedCards || !Array.isArray(sealedCards)) return false;
      const sealedCard = sealedCards.find(sc =>
        sc?.card?.rank === card.rank && sc?.card?.suit === card.suit
      );
      return sealedCard?.seal?.trigger === SEAL_TRIGGER.HAND;
    });
    const hasHandSeal = handSealCards.length > 0;
    const sealFadeDelay = hasHandSeal ? 200 : 0;

    // If HAND seal, trigger fade animation first
    if (hasHandSeal && setFadingSeals) {
      setFadingSeals(handSealCards.map(c => `${c.rank}-${c.suit}`));
    }

    // Sound is now played via server response in handleCardAnimation
    // to allow jokers like Kleiner Furz to replace the sound

    // Calculate target position (center of last-play area)
    const targetRect = lastPlayRef.current?.getBoundingClientRect();
    const targetX = targetRect ? targetRect.left + targetRect.width / 2 : window.innerWidth / 2;
    const targetY = targetRect ? targetRect.top + targetRect.height / 2 : window.innerHeight / 2;

    // Get positions of selected cards and calculate offsets to target
    const cardsWithOffsets = selectedCards.map(card => {
      const cardKey = `${card.rank}-${card.suit}`;
      const cardElement = document.querySelector(`[data-card-key="${cardKey}"]`);
      if (cardElement) {
        const cardRect = cardElement.getBoundingClientRect();
        const cardCenterX = cardRect.left + cardRect.width / 2;
        const cardCenterY = cardRect.top + cardRect.height / 2;
        return {
          ...card,
          offsetX: targetX - cardCenterX,
          offsetY: targetY - cardCenterY
        };
      }
      return { ...card, offsetX: 0, offsetY: -250 };
    });

    // Start card fly animation after seal fade (if any)
    setTimeout(() => {
      // Clear fading seals
      if (setFadingSeals) setFadingSeals([]);
      // Mark cards as playing with their offsets
      setPlayingCards(cardsWithOffsets);
    }, sealFadeDelay);

    // Send action after animation completes (fly animation = 250ms)
    // NOTE: Don't clear playingCards here - let hand update clear it
    // This prevents cards from reappearing briefly before server confirms the play
    setTimeout(() => {
      socketService.playerAction('play', selectedCards, getForPlayerIndex());
      setSelectedCards([]);
      // playingCards will be cleared when hand changes (see PlayingPhase useEffect)
    }, sealFadeDelay + 250);
  }, [selectedCards, setSelectedCards, getForPlayerIndex, setPlayingCards, lastPlayRef, sealedCards, setFadingSeals]);

  /**
   * Karte ziehen oder passen
   */
  const makeDrawOrPass = useCallback(() => {
    if (isRoundStarter) {
      soundService.play('error');
      if (flashMessage) flashMessage('Runden-Starter darf nicht passen!', 'error', 2000);
      return;
    }

    const forPlayerIndex = getForPlayerIndex();

    if (gameState.canDraw) {
      // Sound is played in cardAnimation handler when card arrives
      socketService.playerAction('draw', null, forPlayerIndex);
      // No flash message - game state update will handle it
    } else {
      soundService.play('card_pass');
      socketService.playerAction('pass', null, forPlayerIndex);
      // No flash message - game state update will handle it
    }
  }, [isRoundStarter, getForPlayerIndex, gameState, flashMessage]);

  /**
   * Schild einsetzen um zu passen
   */
  const useShieldToPass = useCallback(() => {
    if (isRoundStarter) {
      if (flashMessage) flashMessage('Runden-Starter darf nicht passen!', 'error', 2000);
      return;
    }

    socketService.shieldPass(getForPlayerIndex());
    if (flashMessage) flashMessage('Schild eingesetzt!', 'effect', 2000);
  }, [isRoundStarter, getForPlayerIndex, flashMessage]);

  /**
   * Schild-Verwendungen des aktuellen Spielers abrufen
   */
  const getCurrentPlayerShieldUses = useCallback(() => {
    if (activePlayerIndex === null || activePlayerIndex === undefined) return 0;
    return shieldUses[activePlayerIndex] || 0;
  }, [activePlayerIndex, shieldUses]);

  /**
   * Nachziehstapel spähen (Sneak Peak Siegel)
   */
  const usePeekDrawPile = useCallback(() => {
    socketService.peekDrawPile(getForPlayerIndex());
    if (flashMessage) flashMessage('Spähe Stapel...', 'action', 1500);
  }, [getForPlayerIndex, flashMessage]);

  /**
   * Späh-Verwendungen des aktuellen Spielers abrufen
   */
  const getCurrentPlayerPeekUses = useCallback(() => {
    if (activePlayerIndex === null || activePlayerIndex === undefined) return 0;
    return peekUses?.[activePlayerIndex] || 0;
  }, [activePlayerIndex, peekUses]);

  /**
   * Siegelring verwenden - Karten mit zufälligen Common-Siegeln versehen
   */
  const useSealRing = useCallback(() => {
    if (selectedCards.length < 1 || selectedCards.length > 3) {
      soundService.play('error');
      if (flashMessage) flashMessage('Wähle 1-3 Karten zum Versiegeln!', 'error', 2000);
      return;
    }

    soundService.play('seal_attach');
    socketService.applySealRing(selectedCards, getForPlayerIndex());
    if (flashMessage) flashMessage('Siegel werden angebracht...', 'effect', 2000);
    setSelectedCards([]);
  }, [selectedCards, setSelectedCards, getForPlayerIndex, flashMessage]);

  /**
   * Siegelring-Verwendungen des aktuellen Spielers abrufen
   */
  const getCurrentPlayerSealRingUses = useCallback(() => {
    if (activePlayerIndex === null || activePlayerIndex === undefined) return 0;
    return sealRingUses?.[activePlayerIndex] || 0;
  }, [activePlayerIndex, sealRingUses]);

  /**
   * Magnet verwenden - höchste Karte aus dem Stapel ziehen
   */
  const useMagnet = useCallback(() => {
    soundService.play('card_draw');
    socketService.useMagnet(getForPlayerIndex());
    if (flashMessage) flashMessage('Ziehe höchste Karte...', 'effect', 2000);
  }, [getForPlayerIndex, flashMessage]);

  /**
   * Magnet-Verwendungen des aktuellen Spielers abrufen
   */
  const getCurrentPlayerMagnetUses = useCallback(() => {
    if (activePlayerIndex === null || activePlayerIndex === undefined) return 0;
    return magnetUses?.[activePlayerIndex] || 0;
  }, [activePlayerIndex, magnetUses]);

  /**
   * Swap verwenden - eine Handkarte gegen eine zufällige Stapelkarte tauschen
   * Verwendet die erste ausgewählte Karte wenn keine explizit übergeben wird
   */
  const performSwap = useCallback(() => {
    const card = selectedCards[0];
    if (!card) return;
    soundService.play('card_draw');
    socketService.useSwap(card, getForPlayerIndex());
    if (flashMessage) flashMessage('Tausche Karte...', 'action', 1500);
    // Clear selection after swap
    setSelectedCards([]);
  }, [selectedCards, getForPlayerIndex, flashMessage, setSelectedCards]);

  /**
   * Swap-Verwendungen des aktuellen Spielers abrufen
   */
  const getCurrentPlayerSwapUses = useCallback(() => {
    if (activePlayerIndex === null || activePlayerIndex === undefined) return 0;
    return swapUses?.[activePlayerIndex] || 0;
  }, [activePlayerIndex, swapUses]);

  /**
   * Glücksgriff verwenden - zufälliges Siegel aus Stash auf ausgewählte Karte anwenden
   * Verwendet die erste ausgewählte Karte
   */
  const useGluecksgriff = useCallback(() => {
    const card = selectedCards[0];
    if (!card) return;
    soundService.play('seal_attach');
    socketService.useGluecksgriff(card, getForPlayerIndex());
    if (flashMessage) flashMessage('Glücksgriff! 🎲', 'action', 1500);
    // Clear selection after use
    setSelectedCards([]);
  }, [selectedCards, getForPlayerIndex, flashMessage, setSelectedCards]);

  /**
   * Glücksgriff-Verwendungen des aktuellen Spielers abrufen
   */
  const getCurrentPlayerGluecksgriffUses = useCallback(() => {
    if (activePlayerIndex === null || activePlayerIndex === undefined) return 0;
    return gluecksgriffUses?.[activePlayerIndex] || 0;
  }, [activePlayerIndex, gluecksgriffUses]);

  /**
   * Stash des aktuellen Spielers abrufen (für Glücksgriff-Verfügbarkeitsprüfung)
   */
  const getCurrentPlayerStash = useCallback(() => {
    if (activePlayerIndex === null || activePlayerIndex === undefined) return [];
    return stash?.[activePlayerIndex] || [];
  }, [activePlayerIndex, stash]);

  return {
    makePlay,
    makeDrawOrPass,
    useShieldToPass,
    getCurrentPlayerShieldUses,
    usePeekDrawPile,
    getCurrentPlayerPeekUses,
    useSealRing,
    getCurrentPlayerSealRingUses,
    useMagnet,
    getCurrentPlayerMagnetUses,
    performSwap,
    getCurrentPlayerSwapUses,
    useGluecksgriff,
    getCurrentPlayerGluecksgriffUses,
    getCurrentPlayerStash,
  };
}

export default useGameActions;
